import React from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { FormSaveFooter } from "../../../../common/controls/forms/FormSaveFooter";

import FormExtras from "../../../../common/components/formExtras";
import { TenantLlmConfigCreateStore } from "../../store";
import { InputReference } from "../../../common/controls";
import { CreateEntityDialog } from "../../../common/controls";
import { LlmProviderCreate } from "../../../llmProvider/components/create/LlmProviderCreate";
import { LlmProviderCreateStore } from "../../../llmProvider/store";
import { InputString } from "../../../common/controls";
import { InputSecret } from "../../../common/controls";
import { InputBoolean } from "../../../common/controls";
import { InputNumber } from "../../../common/controls";
import { FormControl } from "@mui/material";

export type TenantLlmConfigCreateFormProps = {
  onSave?: () => void;
  onCancel?: () => void;
  /** Mientras se guarda, Guardar queda deshabilitado. */
  saving?: boolean;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
  providerSelectedValue?: { displayId: string; id: string } | null;
  onProviderSelected?: (
    value: { displayId: string; id: string } | null
  ) => void;
  onProviderChange?: (value: string) => void;
  providerOptions?: { displayId: string; id: string }[];
  providerError?: string;
  modelSelectedValue?: { displayId: string; id: string } | null;
  onModelChange?: (value: string) => void;
  model: string;
  modelError?: string;
  apiKeySelectedValue?: { displayId: string; id: string } | null;
  onApiKeyChange?: (value: string) => void;
  apiKey: string;
  apiKeyError?: string;
  baseUrlSelectedValue?: { displayId: string; id: string } | null;
  onBaseUrlChange?: (value: string) => void;
  baseUrl: string;
  baseUrlError?: string;
  enabledSelectedValue?: { displayId: string; id: string } | null;
  onEnabledChange?: (value: boolean) => void;
  enabled: boolean;
  enabledError?: string;
  prioritySelectedValue?: { displayId: string; id: string } | null;
  onPriorityChange?: (value: number) => void;
  priority: number;
  priorityError?: string;
  maxTokensSelectedValue?: { displayId: string; id: string } | null;
  onMaxTokensChange?: (value: number | undefined) => void;
  maxTokens: number | undefined;
  maxTokensError?: string;
  temperatureSelectedValue?: { displayId: string; id: string } | null;
  onTemperatureChange?: (value: number | undefined) => void;
  temperature: number | undefined;
  temperatureError?: string;
  debugFullModeSelectedValue?: { displayId: string; id: string } | null;
  onDebugFullModeChange?: (value: boolean) => void;
  debugFullMode: boolean;
  debugFullModeError?: string;
  tenantLlmConfigCreateStore: TenantLlmConfigCreateStore;
};

export const TenantLlmConfigCreateForm: React.FC<TenantLlmConfigCreateFormProps> =
  observer(
    ({
      onSave = () => {},
      onCancel = () => {},
      saving = false,
      showToolbox = true,
      showSaveFooter,
      onProviderSelected = () => {},
      providerSelectedValue = null,
      providerOptions = [],
      providerError,
      onProviderChange = () => {},
      model = "",
      modelError,
      onModelChange = () => {},
      apiKey = "",
      apiKeyError,
      onApiKeyChange = () => {},
      baseUrl = "",
      baseUrlError,
      onBaseUrlChange = () => {},
      enabled = false,
      enabledError,
      onEnabledChange = () => {},
      priority = 0,
      priorityError,
      onPriorityChange = () => {},
      maxTokens = undefined,
      maxTokensError,
      onMaxTokensChange = () => {},
      temperature = undefined,
      temperatureError,
      onTemperatureChange = () => {},
      debugFullMode = false,
      debugFullModeError,
      onDebugFullModeChange = () => {},
      tenantLlmConfigCreateStore,
    }) => {
      const rootStore = useRootStore();
      const { langStore } = rootStore;

      return (
        <Grid>
          {showToolbox && (
            <FormToolbox
              saveAndCloseLabel={langStore.saveEntity}
              closeLabel={langStore.cancelNewEntity}
              saveForm={() => onSave()}
              saveDisabled={saving}
              closeForm={() => onCancel()}
            />
          )}
          <Box>
            <FormExtras
              entity="TenantLlmConfig"
              mode="create"
              values={{}}
              options={{ provider: providerOptions }}
              onPatch={(campo: string, valor: any) => {
                switch (campo) {
                  case "provider":
                    onProviderSelected?.(valor);
                    break;
                  case "model":
                    onModelChange?.(valor);
                    break;
                  case "apiKey":
                    onApiKeyChange?.(valor);
                    break;
                  case "baseUrl":
                    onBaseUrlChange?.(valor);
                    break;
                  case "enabled":
                    onEnabledChange?.(valor);
                    break;
                  case "priority":
                    onPriorityChange?.(valor);
                    break;
                  case "maxTokens":
                    onMaxTokensChange?.(valor);
                    break;
                  case "temperature":
                    onTemperatureChange?.(valor);
                    break;
                  case "debugFullMode":
                    onDebugFullModeChange?.(valor);
                    break;
                }
              }}
            />
            <GroupContainer
              id="null"
              errorText={tenantLlmConfigCreateStore.getCardErrors("null")}
              title={langStore.groupTenantLlmConfigLabel}
              collapsible={false}
            >
              <FormControl fullWidth>
                <InputReference
                  id="provider"
                  label={langStore.TenantLlmConfigProviderLabel}
                  autoFocus
                  error={Boolean(providerError)}
                  helperText={
                    providerError ||
                    tenantLlmConfigCreateStore.warnings["provider"]?.message
                  }
                  selectedValue={providerSelectedValue}
                  onChange={(value: string) => onProviderChange(value)}
                  onSelect={(value: { displayId: string; id: string } | null) =>
                    onProviderSelected(value)
                  }
                  options={providerOptions}
                  description={langStore.TenantLlmConfigProviderDescription}
                  canCreate={rootStore.authStore.hasPermissions(
                    "llmProvider_create"
                  )}
                  renderCreateDialog={(dialogProps) => (
                    <CreateEntityDialog
                      {...dialogProps}
                      createStore={(rootStore) =>
                        new LlmProviderCreateStore(rootStore)
                      }
                      renderForm={(store) => (
                        <LlmProviderCreate
                          llmProviderCreateStore={store}
                          showToolbox={false}
                          showSaveFooter={false}
                        />
                      )}
                      saveMethod="saveNewLlmProvider"
                      cancelMethod="cancelNewLlmProvider"
                    />
                  )}
                />

                <InputString
                  value={model}
                  error={Boolean(modelError)}
                  warning={
                    !modelError &&
                    Boolean(tenantLlmConfigCreateStore.warnings["model"])
                  }
                  helperText={
                    modelError ||
                    tenantLlmConfigCreateStore.warnings["model"]?.message
                  }
                  label={langStore.TenantLlmConfigModelLabel}
                  onChange={(value: string) => onModelChange(value)}
                  description={langStore.TenantLlmConfigModelDescription}
                />

                <InputSecret
                  value={apiKey}
                  error={Boolean(apiKeyError)}
                  helperText={
                    apiKeyError ||
                    tenantLlmConfigCreateStore.warnings["apiKey"]?.message
                  }
                  label={langStore.TenantLlmConfigApiKeyLabel}
                  onChange={(value: string) => onApiKeyChange(value)}
                  description={langStore.TenantLlmConfigApiKeyDescription}
                />

                <InputString
                  value={baseUrl}
                  error={Boolean(baseUrlError)}
                  warning={
                    !baseUrlError &&
                    Boolean(tenantLlmConfigCreateStore.warnings["baseUrl"])
                  }
                  helperText={
                    baseUrlError ||
                    tenantLlmConfigCreateStore.warnings["baseUrl"]?.message
                  }
                  label={langStore.TenantLlmConfigBaseUrlLabel}
                  onChange={(value: string) => onBaseUrlChange(value)}
                  nullText={langStore.optionalFieldHint}
                  description={langStore.TenantLlmConfigBaseUrlDescription}
                />
                <InputBoolean
                  value={enabled}
                  error={Boolean(enabledError)}
                  helperText={
                    enabledError ||
                    tenantLlmConfigCreateStore.warnings["enabled"]?.message
                  }
                  label={langStore.TenantLlmConfigEnabledLabel}
                  description={langStore.TenantLlmConfigEnabledDescription}
                  onChange={(value: boolean) => onEnabledChange(value)}
                />
                <InputNumber
                  value={priority}
                  error={Boolean(priorityError)}
                  warning={
                    !priorityError &&
                    Boolean(tenantLlmConfigCreateStore.warnings["priority"])
                  }
                  helperText={
                    priorityError ||
                    tenantLlmConfigCreateStore.warnings["priority"]?.message
                  }
                  allowNegative={false}
                  label={langStore.TenantLlmConfigPriorityLabel}
                  decimalScale={0}
                  description={langStore.TenantLlmConfigPriorityDescription}
                  onChange={(value: number) => onPriorityChange(value)}
                />
                <InputNumber
                  value={maxTokens}
                  nullable={true}
                  error={Boolean(maxTokensError)}
                  warning={
                    !maxTokensError &&
                    Boolean(tenantLlmConfigCreateStore.warnings["maxTokens"])
                  }
                  helperText={
                    maxTokensError ||
                    tenantLlmConfigCreateStore.warnings["maxTokens"]?.message
                  }
                  allowNegative={false}
                  label={langStore.TenantLlmConfigMaxTokensLabel}
                  decimalScale={0}
                  nullText={langStore.optionalFieldHint}
                  description={langStore.TenantLlmConfigMaxTokensDescription}
                  onChange={(value: number | undefined) =>
                    onMaxTokensChange(value)
                  }
                />
                <InputNumber
                  value={temperature}
                  nullable={true}
                  error={Boolean(temperatureError)}
                  warning={
                    !temperatureError &&
                    Boolean(tenantLlmConfigCreateStore.warnings["temperature"])
                  }
                  helperText={
                    temperatureError ||
                    tenantLlmConfigCreateStore.warnings["temperature"]?.message
                  }
                  allowNegative={true}
                  label={langStore.TenantLlmConfigTemperatureLabel}
                  decimalScale={2}
                  nullText={langStore.optionalFieldHint}
                  description={langStore.TenantLlmConfigTemperatureDescription}
                  onChange={(value: number | undefined) =>
                    onTemperatureChange(value)
                  }
                />
                <InputBoolean
                  value={debugFullMode}
                  error={Boolean(debugFullModeError)}
                  helperText={
                    debugFullModeError ||
                    tenantLlmConfigCreateStore.warnings["debugFullMode"]
                      ?.message
                  }
                  label={langStore.TenantLlmConfigDebugFullModeLabel}
                  description={
                    langStore.TenantLlmConfigDebugFullModeDescription
                  }
                  onChange={(value: boolean) => onDebugFullModeChange(value)}
                />
              </FormControl>
            </GroupContainer>
          </Box>
          {(showSaveFooter ?? !showToolbox) && (
            <FormSaveFooter
              onSave={() => onSave()}
              label={langStore.saveEntity}
              disabled={saving}
            />
          )}
        </Grid>
      );
    }
  );
